import { useQuery } from '@tanstack/react-query'
import {
	CycleIcon,
	FormsIcon,
	HighlightedIcon,
	Label,
	MetricCard,
	PieChartIcon,
	RolesIcon,
	resolveCycleStatus,
	SettingsIcon,
	Text,
	UsersIcon,
} from '@viapeople/components'
import { useCallback, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { FeatureFlagDemo } from '../../../components/FeatureFlagDemo'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { trpc } from '../../../utils/trpc'
import type { Cycle, CycleSearchInput, CycleSearchOutput } from '../../cycles/types'
import { useOrganizationCyclesStore } from '../stores/organizationCyclesStore'
import type { CycleStatsProps, Organization } from '../types'

interface CycleOverviewSectionProps {
	stats: CycleStatsProps
}

const CycleOverviewSection = ({ stats }: CycleOverviewSectionProps) => (
	<div className='flex gap-x-5 text-start items-center'>
		<HighlightedIcon icon={CycleIcon} />
		<div className='w-30'>
			<Text variant='xsmall' className='mb-1'>
				Cycles created
			</Text>
			<Text variant='large' weight='semibold'>
				{stats.created}
			</Text>
		</div>
		<div className='w-30'>
			<Text variant='xsmall' className='mb-1'>
				In progress
			</Text>
			<Text variant='large' weight='semibold'>
				{stats.inProgress}
			</Text>
		</div>
		<div className='w-30'>
			<Text variant='xsmall' className='mb-1'>
				Completed
			</Text>
			<Text variant='large' weight='semibold'>
				{stats.completed}
			</Text>
		</div>
	</div>
)

interface MetricsProps {
	totalUsers: string | number
	totalRoles: string | number
	totalForms: string | number
	onUsersClick?: () => void
	onRolesClick?: () => void
	onFormsClick?: () => void
	onModuleSettingsClick?: () => void
}

const MetricsSection = ({
	totalUsers,
	totalRoles,
	totalForms,
	onUsersClick,
	onRolesClick,
	onFormsClick,
	onModuleSettingsClick,
}: MetricsProps) => (
	<div className='flex flex-col w-[30%] min-w-65 max-w-85 ml-6 gap-y-6'>
		<button type='button' onClick={onUsersClick} className='cursor-pointer'>
			<MetricCard icon={UsersIcon} title='Total users' value={totalUsers.toString()} />
		</button>
		<button type='button' onClick={onRolesClick} className='cursor-pointer'>
			<MetricCard icon={RolesIcon} title='Total roles' value={totalRoles.toString()} />
		</button>
		<button type='button' onClick={onFormsClick} className='cursor-pointer'>
			<MetricCard icon={FormsIcon} title='Total forms' value={totalForms.toString()} />
		</button>
		<button type='button' onClick={onModuleSettingsClick} className='cursor-pointer'>
			<MetricCard icon={SettingsIcon} title='Module Settings' value='Configure' />
		</button>
	</div>
)

export const OrganizationOverviewTab = () => {
	const { organization } = useOutletContext<{ organization: Organization }>()
	const id = organization.id
	const navigate = useNavigate()

	const {
		data: cyclesData,
		pagination,
		searchParams,
		fetchCycles,
		setPage,
		setSearch,
		setSort,
	} = useOrganizationCyclesStore()

	useEffect(() => {
		fetchCycles(organization.id)
	}, [organization.id, fetchCycles])

	const { data: organizationStats } = useQuery({
		...trpc.admin.organizations.getStatsById.queryOptions({
			organizationId: organization.id,
		}),
		enabled: !!organization.id,
	})

	const cycleStats = {
		created: organizationStats?.cycles.created ?? 0,
		inProgress: organizationStats?.cycles.inProgress ?? 0,
		completed: organizationStats?.cycles.completed ?? 0,
	}

	const handleRowClick = (cycle: Cycle) => navigate(`/organizations/${organization.id}/cycles/${cycle.id}/general`)
	const handleUsersClick = () => navigate(`/organizations/${id}/users`)
	const handleRolesClick = () => navigate(`/organizations/${id}/roles`)
	const handleFormsClick = () => navigate(`/organizations/${id}/forms`)
	const handleModuleSettingsClick = () => navigate(`/organizations/${id}/module-settings`)

	const handleSearchChange = useCallback(
		(options: Partial<CycleSearchInput>) => {
			if (options.search !== undefined) {
				setSearch(options.search)
			}
		},
		[setSearch],
	)

	const handleSortChange = useCallback(
		(sortBy: string, order: 'asc' | 'desc') => {
			setSort(sortBy, order)
		},
		[setSort],
	)

	const handlePageChange = useCallback(
		(page: number) => {
			setPage(page)
		},
		[setPage],
	)

	const columns: Column<CycleSearchOutput>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 1,
			sortable: true,
			render: (cycle: Cycle) => (
				<Text variant='xsmall' weight='semibold' className='text-primary-600 col-span-3'>
					{cycle.name}
				</Text>
			),
		},
		{
			field: 'createdAt',
			label: 'Created at',
			colSpan: 1,
			sortable: true,
			render: (cycle: Cycle) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{cycle.createdAt ? new Date(cycle.createdAt).toLocaleDateString() : '-'}
				</Text>
			),
		},
		{
			field: 'participantsCount',
			label: 'Self Evaluators',
			colSpan: 1,
			sortable: false,
			render: ({ participantsCount }) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{`${participantsCount} Self Evaluators`}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: ({ status }: Cycle) => (
				<div className='flex'>
					<Label
						className='self-start'
						color={status === 'completed' ? 'green' : status === 'in-progress' ? 'yellow' : 'default'}>
						{resolveCycleStatus(status)}
					</Label>
				</div>
			),
		},
	]

	const tableData = {
		data: cyclesData,
		meta: {
			page: pagination.page,
			perPage: pagination.perPage,
			total: pagination.total,
			totalInPage: pagination.totalInPage,
		},
	}

	return (
		<div className='flex flex-col px-6'>
			<FeatureFlagDemo organizationId={organization.id} />
			<div className='flex'>
				<div className='flex-1 self-start border border-neutral-200 bg-neutral-0 rounded-lg shadow-xl'>
					{organizationStats && (
						<div className='p-6'>
							<CycleOverviewSection stats={cycleStats} />
						</div>
					)}
					<GenericTable<CycleSearchOutput, CycleSearchInput>
						columns={columns}
						data={tableData}
						searchValue={searchParams.search}
						onSearchChange={handleSearchChange}
						onSortChange={handleSortChange}
						externalCurrentPage={pagination.page}
						onPageChange={handlePageChange}
						externalTotalItems={pagination.total}
						onRowClick={handleRowClick}
						emptyState={{
							icon: PieChartIcon,
							title: 'No overview data to display',
							description: 'Get started by creating a cycle',
							actionLabel: 'Create cycle',
							onAction: () => navigate(`/organizations/${organization.id}/cycles/create/general`),
						}}
					/>
				</div>
				{organizationStats && (
					<MetricsSection
						totalUsers={organizationStats.usersCount}
						totalForms={organizationStats.formsCount}
						totalRoles={organizationStats.rolesCount}
						onUsersClick={handleUsersClick}
						onRolesClick={handleRolesClick}
						onFormsClick={handleFormsClick}
						onModuleSettingsClick={handleModuleSettingsClick}
					/>
				)}
			</div>
		</div>
	)
}
